import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';
import type {
  CreateSupplierRequest,
  ListSuppliersResponse,
  PinAsinRequest,
  Supplier,
  SupplierDetail,
  SuppliersForAsinResponse,
  UpdateSupplierRequest,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export interface SuppliersListParams {
  status?: string;
  hidePassed?: boolean;
}

function buildQueryString(params: SuppliersListParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.hidePassed) search.set('hidePassed', 'true');
  const s = search.toString();
  return s ? `?${s}` : '';
}

export function suppliersQuery(params: SuppliersListParams = {}) {
  return queryOptions({
    queryKey: ['suppliers', params] as const,
    queryFn: () => apiFetch<ListSuppliersResponse>(`/api/suppliers${buildQueryString(params)}`),
    staleTime: 15_000,
  });
}

export function supplierQuery(id: string) {
  return queryOptions({
    queryKey: ['supplier', id] as const,
    queryFn: () => apiFetch<SupplierDetail>(`/api/suppliers/${id}`),
    staleTime: 15_000,
  });
}

export function suppliersForAsinQuery(asin: string) {
  return queryOptions({
    queryKey: ['suppliers-for-asin', asin] as const,
    queryFn: () => apiFetch<SuppliersForAsinResponse>(`/api/products/${asin}/suppliers`),
    staleTime: 15_000,
  });
}

export function useSuppliers(params: SuppliersListParams = {}) {
  return useQuery(suppliersQuery(params));
}

export function useSupplier(id: string) {
  return useQuery(supplierQuery(id));
}

export function useSuppliersForAsin(asin: string) {
  return useQuery(suppliersForAsinQuery(asin));
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSupplierRequest) =>
      apiFetch<Supplier>('/api/suppliers', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSupplierRequest) =>
      apiFetch<Supplier>(`/api/suppliers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      qc.invalidateQueries({ queryKey: ['supplier', id] });
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<null>(`/api/suppliers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function usePinAsin(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PinAsinRequest) =>
      apiFetch<null>(`/api/suppliers/${supplierId}/asins`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['supplier', supplierId] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      qc.invalidateQueries({ queryKey: ['suppliers-for-asin', variables.asin] });
    },
  });
}

export function useUnpinAsin(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asin: string) =>
      apiFetch<null>(`/api/suppliers/${supplierId}/asins/${asin}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, asin) => {
      qc.invalidateQueries({ queryKey: ['supplier', supplierId] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      qc.invalidateQueries({ queryKey: ['suppliers-for-asin', asin] });
    },
  });
}
